/**
 * Formatting helpers.
 *
 * Two conventions from the API matter here and both are deliberately *not*
 * handled with toLocale* :
 *
 *   - A date field (attendance date, joining date, holiday) is midnight UTC of a
 *     calendar day. Running it through a local formatter shifts it a day backwards
 *     for anyone west of UTC, so day-level values are sliced off the ISO string.
 *   - A time field (firstInAt, lastOutAt) is a wall-clock instant stored in UTC —
 *     the clock face the employee actually saw. It is read back with getUTC*.
 */

export const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** '2026-08-19T00:00:00.000Z' | Date -> '2026-08-19', without a timezone shift. */
export function isoDay(value) {
  if (!value) return null;
  if (typeof value === 'string') return value.slice(0, 10);
  return new Date(value).toISOString().slice(0, 10);
}

/** '2026-08-19' -> Date at midnight UTC. */
export const dayToDate = (day) => new Date(`${isoDay(day)}T00:00:00.000Z`);

export const todayISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

export function addDays(day, n) {
  const d = dayToDate(day);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(from, to) {
  return Math.round((dayToDate(to) - dayToDate(from)) / 86_400_000);
}

/** '2026-08-19' -> '19 Aug 2026' */
export function fmtDate(value) {
  const day = isoDay(value);
  if (!day) return '—';
  const d = dayToDate(day);
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** '2026-08-19' -> '19 Aug' */
export function fmtDateShort(value) {
  const day = isoDay(value);
  if (!day) return '—';
  const d = dayToDate(day);
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
}

export function dayOfWeek(value) {
  const day = isoDay(value);
  if (!day) return '';
  return DOW[dayToDate(day).getUTCDay()];
}

/** Wall-clock time out of a UTC-stored instant: '2026-08-19T09:13:00.000Z' -> '09:13' */
export function fmtTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/**
 * Punch / attendance timestamps are wall-clock faces stored as UTC — not true
 * instants. Show the year so "10 Sep 20:04" is never read as year 20:04.
 * '2026-09-10T14:34:00.000Z' -> '10 Sep 2026 14:34'
 */
export function fmtWallInstant(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${d.getUTCFullYear()} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/** Audit timestamps are true instants, so those DO get the viewer's timezone. */
export function fmtInstant(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 510 -> '8h 30m' */
export function hhmm(minutes) {
  const m = Math.round(Number(minutes) || 0);
  if (!m) return '—';
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * Working hours from punch span: out − in (minutes).
 * Null when either punch is missing or the span is invalid.
 */
export function workingMinutesFromInOut(firstInAt, lastOutAt) {
  if (!firstInAt || !lastOutAt) return null;
  const inMs = new Date(firstInAt).getTime();
  const outMs = new Date(lastOutAt).getTime();
  if (Number.isNaN(inMs) || Number.isNaN(outMs) || outMs < inMs) return null;
  return Math.round((outMs - inMs) / 60000);
}

const OPEN_REMOTE_CAP_MINUTES = 18 * 60;

/**
 * Hours to show on an attendance row.
 * Closed days use last out − first in, same for office and WFH.
 * A home / web day that is still open (checked in, no out yet) uses time since check-in.
 */
export function attendanceWorkingMinutes(record, now = Date.now()) {
  if (!record) return null;
  const span = workingMinutesFromInOut(record.firstInAt, record.lastOutAt);
  if (span != null) return span;
  const stored = Number(record.workMinutes);
  if (stored > 0) return Math.round(stored);
  const remote = record.workMode === 'WFH' || record.source === 'WEB_GEO';
  if (!remote || !record.firstInAt || record.lastOutAt) return null;
  const start = new Date(record.firstInAt).getTime();
  if (Number.isNaN(start)) return null;
  const elapsed = Math.round((now - start) / 60000);
  if (elapsed < 0 || elapsed > OPEN_REMOTE_CAP_MINUTES) return null;
  return elapsed;
}

/** Always show hours even when 0 — used next to Present / Absent badges. */
export function fmtWorkingHrs(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return null;
  const m = Math.max(0, Math.round(Number(minutes)));
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** '09:30' -> 570 */
export function timeToMinutes(value) {
  if (!value) return null;
  const [h, m] = String(value).split(':').map(Number);
  return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
}

export function minutesToTime(mins) {
  const m = Math.max(0, Math.round(mins));
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

export const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

export function inrShort(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return inr(v);
}

export const num = (n) => (Number(n) || 0).toLocaleString('en-IN');

export const pct = (n) => `${Math.round(Number(n) || 0)}%`;

/** ON_LEAVE -> 'On leave' */
export const humanise = (value) => String(value ?? '')
  .replace(/_/g, ' ')
  .toLowerCase()
  .replace(/^./, (c) => c.toUpperCase());

const EXIT_LABELS = {
  ABSCOND: 'Termination / Abscond',
};

/** Exit type as HR names it. ABSCOND is one option, not two words from the enum. */
export const exitLabel = (value) => EXIT_LABELS[value] ?? humanise(value);

/** "Data Science — Data Titans" when the row sits under a parent department. */
export function departmentLabel(department, rows = []) {
  if (!department) return '';
  const parent = department.parent
    ?? rows.find((row) => row.id === (department.parentId ?? department.parent?.id));
  return parent?.name ? `${parent.name} — ${department.name}` : department.name;
}

/** '2026-08' -> 'August 2026' */
export function fmtPeriod(period) {
  if (!period) return '—';
  const [y, m] = period.split('-').map(Number);
  return `${MONL[m - 1]} ${y}`;
}

export function fmtPeriodShort(period) {
  if (!period) return '—';
  const [, m] = period.split('-').map(Number);
  return MON[m - 1];
}

export const tenureYears = (joining, until) => (daysBetween(isoDay(joining), isoDay(until ?? todayISO())) / 365);

/**
 * Tenure as compact lowercase units for list subtitles: `2y 3m 12d`.
 * Omits zero higher units (e.g. `5m 2d`, `14d`). Returns '' when DOJ is missing.
 */
export function formatTenureShort(joining, until) {
  const from = isoDay(joining);
  const to = isoDay(until ?? todayISO());
  if (!from || !to) return '';
  let days = daysBetween(from, to);
  if (days < 0) days = 0;
  const y = Math.floor(days / 365);
  days -= y * 365;
  const m = Math.floor(days / 30);
  days -= m * 30;
  const parts = [];
  if (y > 0) parts.push(`${y}y`);
  if (m > 0) parts.push(`${m}m`);
  if (days > 0 || parts.length === 0) parts.push(`${days}d`);
  return parts.join(' ');
}

/** Title-case each word: `digital marketing` → `Digital Marketing`. */
function titleCaseWords(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\b([a-z])/g, (ch) => ch.toUpperCase());
}

/** Directory / attendance / dashboard subtitle: `Digital Marketing, 1y 11m 12d`. */
export function personOrgSubline(department, dateOfJoining) {
  const deptRaw = typeof department === 'string'
    ? department.trim()
    : String(department?.name ?? '').trim();
  const dept = titleCaseWords(deptRaw);
  const tenure = formatTenureShort(dateOfJoining); // keep units lowercase: 1y 11m 12d
  const bits = [dept, tenure].filter(Boolean);
  return bits.length ? bits.join(', ') : '';
}

export function relativeDays(day) {
  const n = daysBetween(isoDay(day), todayISO());
  if (n === 0) return 'today';
  if (n === 1) return 'yesterday';
  if (n < 0) return `in ${Math.abs(n)} days`;
  return `${n} days ago`;
}

export const initials = (name) => String(name ?? '?')
  .split(' ').filter(Boolean).slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

export const bytes = (n) => {
  const v = Number(n) || 0;
  if (v >= 1024 * 1024) return `${(v / 1024 / 1024).toFixed(1)} MB`;
  if (v >= 1024) return `${Math.round(v / 1024)} KB`;
  return `${v} B`;
};
